import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { useDesignSystem } from '@zapier/design-system-context';
import { Animation, Colors } from '../../../theme';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { fromEntries } from '../../../utils/fromEntries';
const Styles = labelStyles('Link', {
    root: () => [
        css `
      // Need to manually unset here since these styles may
      // be passed to a component and not directly to an 'a'.
      all: unset;
      box-sizing: border-box;
      text-decoration: underline;
      cursor: pointer;
      transition: all ${Animation.transitionDuration} ease-in-out;
      outline-offset: 1px;
      // Fix Safari 13 issue with 'color' and 'all: unset'
      // (https://bugs.webkit.org/show_bug.cgi?id=158782).
      -webkit-text-fill-color: currentColor;

      outline: 1px solid transparent;

      &[data-color='ocean'] {
        color: ${Colors.blue};

        &:hover {
          color: ${Colors.night};
        }

        &:focus {
          color: ${Colors.blue};
          outline-color: ${Colors.blue};
        }
      }

      &[data-color='white'] {
        color: ${Colors.neutral100};

        &:hover {
          color: ${Colors.neutral500};
        }

        &:focus {
          color: ${Colors.neutral100};
          outline-color: ${Colors.neutral100};
        }
      }

      &[data-color='primary'] {
        color: ${Colors.blue};

        &:hover {
          color: ${Colors.night};
        }

        &:focus {
          color: ${Colors.blue};
          outline-color: ${Colors.blue};
        }
      }

      &[data-color='secondary'] {
        color: ${Colors.neutral100};

        &:hover {
          color: ${Colors.neutral500};
        }

        &:focus {
          color: ${Colors.neutral100};
          outline-color: ${Colors.neutral100};
        }
      }

      &[data-weight='inherit'] {
        font-weight: inherit;
      }

      &[data-weight='normal'] {
        font-weight: 400;
      }

      &[data-weight='bold'] {
        font-weight: 700;
      }
    `,
    ],
});
const DOMAIN_WHITELIST = ['zapier.com', 'zapier-staging.com', 'localhost'];
const isLinkExternal = (href) => {
    if (!href || href.length === 0) {
        return false;
    }
    const matcher = /(^[?#/]|http)|^[^.]+(\/)/;
    let hrefWithProtocol = href;
    if (!matcher.test(href)) {
        hrefWithProtocol = `https://${href}`;
    }
    const baseUrl = 'https://zapier.com';
    let url;
    try {
        url = new URL(hrefWithProtocol, baseUrl);
    }
    catch (_) {
        return true;
    }
    const [topLevelDomain, secondLevelDomain] = url.host
        .split(':')[0]
        .split('.')
        .reverse();
    const domain = secondLevelDomain
        ? `${secondLevelDomain}.${topLevelDomain}`
        : topLevelDomain;
    return !DOMAIN_WHITELIST.includes(domain);
};
const buildRel = (rel, isExternal, noReferrer) => {
    const externalLinkRel = [];
    if (isExternal) {
        externalLinkRel.push('noopener');
    }
    if (isExternal && noReferrer) {
        externalLinkRel.push('noreferrer');
    }
    const propsRel = (rel && rel.split(/\s+/)) || [];
    return [...new Set([...propsRel, ...externalLinkRel])].join(' ') || undefined;
};
export const Link = forwardRef(function Link(props, ref) {
    const { color = 'primary', component: Component, noReferrer = true, weight = 'inherit', } = props;
    const colorMaybeNull = color === 'inherit' ? null : color;
    const trimmedHref = props.href ? props.href.trim() : props.href;
    const { LinkComponent: ContextLink } = useDesignSystem();
    const LinkComponent = Component || ContextLink;
    const dataAttributes = fromEntries(Object.entries(props).filter(([key]) => {
        return key.startsWith('data-');
    }));
    return (isolateComponent(LinkComponent, Object.assign({ "aria-current": props.ariaCurrent, "aria-label": props.ariaLabel, className: props.className, css: props.className ? undefined : Styles.root(), "data-color": props.className ? undefined : colorMaybeNull, "data-weight": props.className ? undefined : weight, href: trimmedHref, id: props.id, name: props.name, onBlur: props.onBlur, onClick: props.onClick, onFocus: props.onFocus, onKeyDown: props.onKeyDown, onMouseDown: props.onMouseDown, onMouseEnter: props.onMouseEnter, onMouseLeave: props.onMouseLeave, onMouseUp: props.onMouseUp, ref: ref, rel: buildRel(props.rel, isLinkExternal(props.href), noReferrer), role: props.role, style: props.style, tabIndex: props.tabIndex, target: props.target, referrerPolicy: props.referrerPolicy, title: props.title }, dataAttributes), props.children));
});
//# sourceMappingURL=Link.js.map
// @zapier/design-system@5.32.7 (b4d49404e)
